import { useFramework } from '../api';

/**
 * A composable for merging control answers and product answers in a way that
 * ensures both product answers and unanswered controls can be displayed - used
 * for when displaying the full assessment.
 *
 * This should never be used directly in components, always wrapped in another
 * composable - like the ones in the profile and assessment stores.
 */
export function useGenerateControlsWithAnswers() {
  const { isChildControl, dependentControls } = useFramework();

  function generateControlsWithAnswers<T extends { controlID: string; productID?: string }>(
    controlIDs: string[] | undefined,
    answersByControlID: Partial<Record<string, T[]>>,
    productIDs: string[],
    // Pending product IDs also includes product IDs that now have answers - used to preserve order
    pendingProductIDs?: { controlID: string; productID: string }[],
  ): { controlID: string; productID?: string; answer?: T }[] {
    // We filter out child controls so that they can be handled below
    const rootControlIDs = controlIDs?.filter((controlID) => !isChildControl(controlID));

    if (!rootControlIDs) return [];

    const controlsIDsWithAnswers: { controlID: string; productID?: string; answer?: T }[] = [];

    function addChildren(controlID: string, productID: string | undefined) {
      const childControlIDs = dependentControls.value[controlID];

      if (!childControlIDs?.length) return;

      for (const childControlID of childControlIDs) {
        const maybeChildAnswer = answersByControlID[childControlID]?.find(
          (answer) => answer.productID === productID,
        );

        controlsIDsWithAnswers.push({
          controlID: childControlID,
          answer: maybeChildAnswer,
          productID,
        });
      }
    }

    for (const controlID of rootControlIDs) {
      const answersForControl = answersByControlID[controlID] || [];

      const orderedAnswers = answersForControl.sort((a, b) => {
        if (a.productID && !b.productID) return 1;
        if (!a.productID && b.productID) return -1;

        // Unless there's somehow multiple org answers, this should never happen
        if (!a.productID || !b.productID) return 0;

        return productIDs.indexOf(a.productID) - productIDs.indexOf(b.productID);
      });

      const pendingProductIDsForControl =
        pendingProductIDs?.filter((p) => p.controlID === controlID).map((p) => p.productID) || [];

      const answersForProducts = new Map<string, T>();

      // If there's no org answer, we need to add an empty one
      if (!orderedAnswers.find((a) => !a.productID)) {
        controlsIDsWithAnswers.push({ controlID });
        addChildren(controlID, undefined);
      }

      for (const answer of orderedAnswers) {
        // If the product answer was added by the user, store it to add in the correct order
        if (answer.productID && pendingProductIDsForControl.includes(answer.productID)) {
          answersForProducts.set(answer.productID, answer);
          continue;
        }

        controlsIDsWithAnswers.push({ controlID, productID: answer.productID, answer });
        addChildren(controlID, answer.productID);
      }

      for (const productID of pendingProductIDsForControl) {
        controlsIDsWithAnswers.push({
          controlID,
          productID,
          answer: answersForProducts.get(productID),
        });
        addChildren(controlID, productID);
      }
    }

    return controlsIDsWithAnswers;
  }

  return { generateControlsWithAnswers };
}
